import { createSlice } from "@reduxjs/toolkit"
import { addMinutes } from "date-fns"
import _ from "lodash"

interface UnfinishedDiary {
	expiresAt: Date
	values: object
}
type UnfinishedDiariesMap = Record<string, UnfinishedDiary>

interface UnfinishedDiariesState {
	unfinishedDiaries: UnfinishedDiariesMap
}

const initialState: UnfinishedDiariesState = {
	unfinishedDiaries: {},
}
/** Represents slice of unfinished (meaning unsubmitted) diaries. Created for user experience purposes, they are persisted into a slice for either 60 minutes or until the survey associated with the task is succesfully submitted. */
export const unfinishedDiariesSlice = createSlice({
	name: "unfinishedDiaries",
	initialState,
	reducers: {
		/** Upserts the new diary values based on event id and task id to persisted slice. Also resets the expiration time for the kept unsubmitted diaries. */
		updateDiaryEntry(state, { payload }: { payload: { eventId: string; taskId: string; values: object } }) {
			const { eventId, taskId, values } = payload
			const key = `${eventId}-${taskId}`
			state.unfinishedDiaries[key] = {
				expiresAt: addMinutes(new Date(), 60),
				values,
			}
		},

		/** Tries to remove associated diary entries by eventid and taskid. Does nothing if can't find them. */
		tryRemoveAssociatedDiaryEntries(state, { payload }: { payload: { eventId: string; taskId: string } }) {
			const { eventId, taskId } = payload
			const key = `${eventId}-${taskId}`

			// Early return if there are no associated diary entries since there is nothing to do in that case
			if (!(key in state.unfinishedDiaries)) {
				return
			}

			_.unset(state.unfinishedDiaries, key)
		},
	},
	selectors: {
		selectNonExpiredUnfinishedDiaries(state, payload: { eventId: string; taskId: string }) {
			const { eventId, taskId } = payload
			const key = `${eventId}-${taskId}`

			if (!(key in state.unfinishedDiaries)) {
				return null
			}

			const { expiresAt, values } = state.unfinishedDiaries[key]!
			if (new Date() > expiresAt) {
				return null
			}

			return values
		},
	},
})

export const { updateDiaryEntry, tryRemoveAssociatedDiaryEntries } = unfinishedDiariesSlice.actions
export const { selectNonExpiredUnfinishedDiaries } = unfinishedDiariesSlice.selectors
